import axios from 'axios';
import _ from 'lodash';

// CW1 sets `createdBy` to this email on auto-generated update entries.
const CW1_UPDATE_CREATED_BY = 'cargowise@monada.ai';
// Defensive HTML markers in case a future CW1 update routes through a different account.
const CW1_HTML_MARKERS = ['Account updated in Cargowise', 'Account created in TMS'];

function isCw1Update(u) {
    if (!u) return false;
    if (u.createdBy === CW1_UPDATE_CREATED_BY) return true;
    if (typeof u.html === 'string' && CW1_HTML_MARKERS.some(m => u.html.includes(m))) return true;
    return false;
}

// True if the account shows any user-driven signal: non-CW1 updates, log entries
// (pipeline/owner/etc are all user-initiated), or reminders/tasks. Tags are not
// considered — too noisy in practice and many CW1-sourced tags slip through the
// POSSIBLE_LABELS classification.
// Used by the JTM workspace cleanup to drop accounts that exist only as CW1 mirrors.
function hasUserDrivenActivity({ account, opportunityAccountIds }) {
    if (!account) return false;
    if ((account.updates || []).some(u => !isCw1Update(u))) return true;
    if ((account.log || []).length > 0) return true;
    if ((account.reminders || []).length > 0) return true;
    if (opportunityAccountIds && opportunityAccountIds.has(account.id)) return true;
    // Keep accounts owned by a real user (not cargowise or admin — means someone claimed this account)
    if (account.owner && account.owner !== CW1_UPDATE_CREATED_BY && account.owner !== 'admin@monada.ai') return true;
    return false;
}

async function getAccountsFromServer({ accesstoken, organizationId, me, onProgress }) {

    // Open the database
    const db = await new Promise((res, rej) => {
        const request = indexedDB.open(`${organizationId}-${me.email}-accounts`, 1);
        request.onerror = (event) => {
            console.error('Why didn\'t you allow my web app to use IndexedDB?!', event);
            rej(event.target.error);
        };
        request.onsuccess = (event) => {
            console.log('[Accounts: getAccountsFromServer] Database opened successfully.');
            res(event.target.result);
        };
        request.onupgradeneeded = (event) => {
            console.log('[Accounts: getAccountsFromServer] Database onupgradeneeded called.');
            const db = event.target.result;
            db.createObjectStore('accounts', { keyPath: 'id' });
        };
    });

    // Get existing opportunities from IndexedDB
    let cache = await new Promise((res, rej) => {
        console.log('[Accounts: getAccountsFromServer] Reading from IndexedDB.');
        const transaction = db.transaction(['accounts'], 'readonly');
        const store = transaction.objectStore('accounts');
        const request = store.getAll();
        request.onsuccess = (event) => {
            console.log('[Accounts: getAccountsFromServer] Done reading', event.target.result.length, 'from IndexedDB.');
            res(event.target.result);
        };
        request.onerror = (event) => {
            rej(event.target.error);
        }
    });

    const latest = (_.max(cache.map(r => parseInt(r.modified))) || 0) + 1;

    const items = [];
    let cursor = null;
    let cacheServer = null;
    let shouldResetLocalDB = false;
    console.log(`[Accounts: getAccountsFromServer] Starting /api/accounts?organizationId=${organizationId}&since=${latest}`);
    do {
        const { data } = await axios(`/api/accounts?organizationId=${organizationId}&accesstoken=${accesstoken}&since=${latest}&cursor=${cursor ? cursor : ''}`);
        if (_.isArray(data)) return data;
        cursor = data.cursor;
        cacheServer = data.cache;
        shouldResetLocalDB = data.shouldResetLocalDB;
        items.push(...data.accounts);
        if (onProgress) onProgress({ total: data.count, count: items.length });
        console.log(`[Accounts: getAccountsFromServer] Read ${items.length} items. Has more? ${cursor ? 'Yes' : 'No'}`);
    } while (cursor);

    // Clear IndexexDB if requested
    if (shouldResetLocalDB) {
        console.log('[Accounts: getAccountsFromServer] Reset requested.');

        // Clear IndexedDB
        const transaction = db.transaction(['accounts'], 'readwrite');
        const store = transaction.objectStore('accounts');
        try {
            await new Promise((res, rej) => {
                const request = store.clear();
                request.onsuccess = (event) => {
                    console.log('[Accounts: getAccountsFromServer] IndexedDB cleared.');
                    res();
                };
                request.onerror = (event) => {
                    console.error('[Accounts: getAccountsFromServer] Error clearing IndexedDB.', event.target.error);
                    rej(event.target.error);
                }
            });
        } catch (e) { }
    }

    const transaction = db.transaction(['accounts'], 'readwrite');
    const store = transaction.objectStore('accounts');
    console.log('[Accounts: getAccountsFromServer] Adding', items.length, 'accounts to IndexedDB.');
    for (let i = 0; i < items.length; i++) {
        const r = items[i];
        try {
            await new Promise((res, rej) => {
                const request = store.put(r); // TODO: Check if exists and update
                request.onsuccess = (event) => {
                    res();
                };
                request.onerror = (event) => {
                    rej(event.target.error);
                }
            });
        } catch (e) { }
    }

    console.log('[Accounts: getAccountsFromServer] Done adding', items.length, 'accounts to IndexedDB.');

    let cacheAccounts = [];
    if (cacheServer) {
        cacheAccounts = (await axios(cacheServer)).data;
    }

    if (shouldResetLocalDB) {
        return _.uniqBy([...items, ...cacheAccounts], 'id').filter(r => r.id !== `${organizationId}-reset`);
    } else {
        return _.uniqBy([...items, ...cache, ...cacheAccounts], 'id').filter(r => r.id !== `${organizationId}-reset`);
    }
}

export { getAccountsFromServer, hasUserDrivenActivity };